import { photos } from '@cwos/api-client';
import { Avatar } from '@cwos/ui-admin';
import type { Person } from './personDirectory.js';

/**
 * A person, rendered the one way this product renders people.
 *
 * `docs/PROFILE-PHOTO-CONTRACT.md` in a component: exactly three fields
 * (`userId`, `displayName`, `photoUpdatedAt`), resolved through
 * `photos.personPhotoSrc`, with initials as the honest default rather than a
 * loading state. `personPhotoSrc` returns null when there is no photo, and
 * `Avatar` renders initials for a null `src` and falls back to initials again
 * if an image that was expected to exist fails to load.
 *
 * THE NAME IS ALWAYS TEXT. The avatar is `aria-hidden` inside `Avatar`, so a
 * photograph is never the only identity signal — which is also why a photo may
 * never influence authentication, authorization, eligibility or matching: it is
 * decoration beside a fact that is already present in words.
 *
 * There is deliberately no `size`-less variant that drops the name, and no prop
 * for a per-domain image. A caller who has only an id calls
 * `personDirectory(...).person(id)` first.
 */
export interface PersonChipProps {
  person: Person;
  size?: number;
  /** Extra words after the name — a role, a scope, a relationship. */
  detail?: string;
}

export function PersonChip({ person, size = 24, detail }: PersonChipProps) {
  return (
    <span className="person-chip">
      <Avatar name={person.displayName} size={size} src={photos.personPhotoSrc(person)} />
      <span className="person-chip-text">
        <span className="person-chip-name">{person.displayName}</span>
        {detail ? <span className="person-chip-detail">{detail}</span> : null}
      </span>
    </span>
  );
}
